/**
 * PWA Auto Updater - Proactive silent automatic updates
 * Detects new versions and reloads BEFORE user encounters chunk errors
 */

import { useRegisterSW } from 'virtual:pwa-register/react';
import { useEffect, useRef } from 'react';

export function PWAAutoUpdater() {
  const reloadAttempted = useRef(false);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      console.log('[PWA] Service worker registered with auto-update');

      // Check for updates every 60 seconds
      if (registration) {
        intervalIdRef.current = setInterval(() => {
          registration.update().catch((error) => {
            console.error('[PWA] Update check failed:', error);
          });
        }, 60000);
      }
    },
    onRegisterError(error) {
      console.error('[PWA] Service worker registration failed:', error);
    },
  });

  // Proactive reload when new version detected
  useEffect(() => {
    if (needRefresh && !reloadAttempted.current) {
      reloadAttempted.current = true;

      console.log(
        '[PWA] New version detected - updating silently in background...'
      );

      // Small delay to ensure SW is ready
      setTimeout(() => {
        updateServiceWorker(true); // true = reload page after update
      }, 1000);
    }
  }, [needRefresh, updateServiceWorker]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);

  // No UI - completely invisible to user
  return null;
}
